import { useEffect, useState } from 'react';
import {
  PageContainer,
  ProForm,
  ProFormText,
  ProFormSelect,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { Card, ColorPicker, Form, Tabs, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createIndustry,
  getIndustry,
  updateIndustry,
  type Industry,
  type TranslationDict,
} from '@/api/industries';

const LANGS: { code: keyof TranslationDict; label: string }[] = [
  { code: 'zh-hans', label: '简体中文' },
  { code: 'zh-hant', label: '繁體中文' },
  { code: 'en', label: 'English' },
  { code: 'th', label: 'ภาษาไทย' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'vi', label: 'Tiếng Việt' },
];

function parseTranslations(value: TranslationDict | string | undefined): TranslationDict {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value;
}

export default function IndustryFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form] = Form.useForm<Industry>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const detail = await getIndustry(id!);
        const nameTranslations = parseTranslations(detail.name_translations);
        const descTranslations = parseTranslations(detail.description_translations);
        form.setFieldsValue({
          ...detail,
          name_translations: nameTranslations as any,
          description_translations: descTranslations as any,
        });
      } catch {
        // 拦截器已提示
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, form]);

  const handleFinish = async (values: any) => {
    const payload: Partial<Industry> = {
      code: values.code,
      status: values.status,
      icon: values.icon || null,
      color: typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || '#1677ff',
      name_translations: values.name_translations,
      description_translations: values.description_translations,
    };
    try {
      if (isEdit) {
        await updateIndustry(id!, payload);
        message.success('已更新');
      } else {
        await createIndustry(payload);
        message.success('已创建');
      }
      navigate('/industries');
    } catch {
      // 拦截器已提示
    }
    return true;
  };

  return (
    <PageContainer header={{ title: isEdit ? '编辑行业' : '新建行业' }}>
      <Card loading={loading}>
        <ProForm<any>
          form={form}
          onFinish={handleFinish}
          submitter={{
            searchConfig: { submitText: isEdit ? '保存' : '创建', resetText: '取消' },
            onReset: () => navigate('/industries'),
          }}
          initialValues={{
            status: 'ACTIVE',
            color: '#1677ff',
            name_translations: {},
            description_translations: {},
          }}
        >
          <ProFormText
            name="code"
            label="行业代码"
            tooltip="英文唯一标识, 创建后不建议修改"
            rules={[
              { required: true, message: '请输入行业代码' },
              { pattern: /^[a-z0-9_-]+$/i, message: '只允许字母/数字/-/_' },
            ]}
            disabled={isEdit}
          />
          <ProFormSelect
            name="status"
            label="状态"
            options={[
              { label: '活跃', value: 'ACTIVE' },
              { label: '未激活', value: 'INACTIVE' },
              { label: '即将上线', value: 'COMING_SOON' },
              { label: '已弃用', value: 'DEPRECATED' },
            ]}
            rules={[{ required: true }]}
          />
          <Form.Item label="主题色" name="color" rules={[{ required: true }]}>
            <ColorPicker format="hex" showText />
          </Form.Item>
          <ProFormText name="icon" label="图标" placeholder="FontAwesome 类名或 URL" />

          <Form.Item label="名称 (多语言)" required style={{ marginBottom: 0 }}>
            <Tabs
              items={LANGS.map((l) => ({
                key: l.code,
                label: l.label,
                children: (
                  <ProFormText
                    name={['name_translations', l.code]}
                    placeholder={`${l.label} 名称`}
                    rules={
                      l.code === 'zh-hans' ? [{ required: true, message: '简体中文名称必填' }] : []
                    }
                  />
                ),
              }))}
            />
          </Form.Item>

          <Form.Item label="描述 (多语言)" style={{ marginBottom: 0 }}>
            <Tabs
              items={LANGS.map((l) => ({
                key: l.code,
                label: l.label,
                children: (
                  <ProFormTextArea
                    name={['description_translations', l.code]}
                    placeholder={`${l.label} 描述`}
                    fieldProps={{ rows: 3 }}
                  />
                ),
              }))}
            />
          </Form.Item>
        </ProForm>
      </Card>
    </PageContainer>
  );
}
